"use client";

import type { IntegrationsContent } from "@/components/landing/registry/content";
import { pickBi } from "@/components/landing/registry/locale";
import {
	Eyebrow,
	MotionReveal,
	SectionHeader,
	useSectionMotion,
} from "@/components/landing/shared";

interface IntegrationsInnerProps {
	content: IntegrationsContent;
	locale: string;
}

/**
 * Header + a grid of integration/partner tiles. Falls back to the
 * localized name's first letter when an admin hasn't uploaded a logo yet.
 */
function IntegrationsInner(prop: IntegrationsInnerProps) {
	const { content, locale } = prop;
	const motion = useSectionMotion();
	const eyebrow = pickBi(content.eyebrow, locale);
	const title = pickBi(content.title, locale);
	const subtitle = pickBi(content.subtitle, locale);

	return (
		<>
			<MotionReveal motion={motion}>
				<SectionHeader
					eyebrow={eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : undefined}
					title={title}
					subtitle={subtitle}
					wrapperClassName="mx-auto max-w-2xl px-6 text-center"
				/>
			</MotionReveal>

			<div className="mx-auto mt-14 grid max-w-5xl grid-cols-2 gap-4 px-6 sm:grid-cols-3 lg:grid-cols-4">
				{content.items.map((item, i) => {
					const name = pickBi(item.name, locale);
					const label = pickBi(item.label, locale);
					return (
						<MotionReveal key={`${i}-${item.name.en}`} motion={motion} delayOffset={120 + i * 60}>
							<div className="card flex h-full flex-col items-center gap-3 p-5 text-center">
								{item.imageUrl ? (
									<img
										src={item.imageUrl}
										alt={label || name}
										className="h-12 w-12 object-contain"
									/>
								) : (
									<div className="grid h-12 w-12 place-items-center rounded-xl bg-surface-2 text-lg font-bold">
										{name.charAt(0)}
									</div>
								)}
								<div>
									<p className="font-semibold">{name}</p>
									{label && <p className="mt-1 text-sm text-muted-foreground">{label}</p>}
								</div>
							</div>
						</MotionReveal>
					);
				})}
			</div>
		</>
	);
}

export default IntegrationsInner;
